import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Chip,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon, ContentPaste as ContentPasteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { RequiredLabel, getFieldErrorSx, getSelectErrorSx, validateForm } from '../../components/common/FormHelpers';
import InternationalPhoneField from '../../components/common/InternationalPhoneField';
import StructuredAddressFields from '../../components/common/StructuredAddressFields';
import { parseWebsiteUrl, combineWebsiteUrl } from '../../utils/websiteUrl';

// Required fields for account form
const REQUIRED_FIELDS = ['company_name', 'account_type'];

const AccountForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [websiteProtocol, setWebsiteProtocol] = useState('https://');
  const [websiteHost, setWebsiteHost] = useState('');
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, existing: null, serverMessage: '' });
  const [sigOpen, setSigOpen] = useState(false);
  const [sigText, setSigText] = useState('');
  const [sigHints, setSigHints] = useState([]);
  const [sigParsed, setSigParsed] = useState(false);
  const [sigFields, setSigFields] = useState({
    company_name: '',
    phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: ''
  });
  const [sigParsing, setSigParsing] = useState(false);
  const [sigSaving, setSigSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '', account_type: 'client', industry: '', website: '', phone: '', fax: '',
    address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US',
    status: 'active', payment_terms: 30, tax_id: '', notes: ''
  });

  const industryChoices = useMemo(() => {
    const industry = formData.industry;
    if (industry && !industryOptions.includes(industry)) {
      return [industry, ...industryOptions];
    }
    return industryOptions;
  }, [formData.industry, industryOptions]);

  useEffect(() => { 
    fetchContacts();
    fetchIndustries();
    if (isEdit) fetchAccount(); 
    else {
      setWebsiteProtocol('https://');
      setWebsiteHost('');
    }
  }, [id]);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts?limit=100&status=active');
      setContacts(response.data.data || []);
    } catch (error) { console.error('Failed to fetch contacts'); }
  };

  const fetchIndustries = async () => {
    try {
      const response = await api.get('/accounts/industries');
      setIndustryOptions(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch industries', error);
      setIndustryOptions([]);
    }
  };

  const fetchAccount = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/accounts/${id}`);
      const accountData = response.data.data;
      setFormData(accountData);
      const { protocol, host } = parseWebsiteUrl(accountData.website);
      setWebsiteProtocol(protocol);
      setWebsiteHost(host);
      if (accountData.Contacts) {
        setSelectedContacts(accountData.Contacts);
      }
    } catch (error) { toast.error('Failed to fetch'); navigate('/accounts'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const patchFormData = (patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    Object.keys(patch).forEach((name) => {
      if (validationErrors[name]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const { isValid, errors } = validateForm(formData, REQUIRED_FIELDS);
    if (!isValid) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields marked with *');
      const firstErrorField = REQUIRED_FIELDS.find(field => errors[field]);
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }
      }
      return;
    }
    
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        website: combineWebsiteUrl(websiteProtocol, websiteHost),
        contact_ids: selectedContacts.map(c => c.id)
      };
      let response;
      if (isEdit) response = await api.put(`/accounts/${id}`, submitData);
      else response = await api.post('/accounts', submitData);
      if (!isEdit && response.status === 202 && response.data?.data?.request_submitted) {
        setDuplicateDialog({
          open: true,
          existing: response.data?.data?.existing_account || null,
          serverMessage:
            response.data?.message ||
            'A company with the same name already exists. Your request was submitted to the administrator for review.'
        });
        setSaving(false);
        return;
      }
      toast.success(`Account ${isEdit ? 'updated' : 'created'} successfully`);
      navigate('/accounts');
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const updateSigField = (name, value) => {
    setSigFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleParseSignature = async () => {
    if (!sigText.trim()) {
      toast.error('Paste the email signature text first.');
      return;
    }
    setSigParsing(true);
    try {
      const res = await api.post('/accounts/parse-signature', { text: sigText });
      const d = res.data?.data || {};
      setSigHints(Array.isArray(d.hints) ? d.hints : []);
      setSigFields({
        company_name: d.company_name || '',
        phone: d.phone || '',
        website: d.website || '',
        address_line1: d.address_line1 || '',
        address_line2: d.address_line2 || '',
        city: d.city || '',
        state: d.state || '',
        zip_code: d.zip_code || '',
        country: d.country || '',
        contact_first_name: d.contact_first_name || '',
        contact_last_name: d.contact_last_name || '',
        contact_email: d.contact_email || ''
      });
      setSigParsed(true);
      setFormData((prev) => ({
        ...prev,
        company_name: d.company_name || prev.company_name,
        phone: d.phone != null && d.phone !== '' ? d.phone : prev.phone,
        address_line1: d.address_line1 || prev.address_line1,
        address_line2: d.address_line2 || prev.address_line2,
        city: d.city || prev.city,
        state: d.state || prev.state,
        zip_code: d.zip_code || prev.zip_code,
        country: d.country || prev.country
      }));
      if (d.website) {
        const { protocol, host } = parseWebsiteUrl(d.website);
        setWebsiteProtocol(protocol);
        setWebsiteHost(host);
      }
      toast.success('Review the extracted values and adjust if needed.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not parse signature');
    } finally {
      setSigParsing(false);
    }
  };

  const handleAddAccountFromSignature = async () => {
    const {
      company_name,
      phone,
      website,
      address_line1,
      address_line2,
      city,
      state,
      zip_code,
      country,
      contact_first_name,
      contact_last_name,
      contact_email
    } = sigFields;
    if (!company_name.trim() || !contact_first_name.trim() || !contact_last_name.trim() || !contact_email.trim()) {
      toast.error('Company name, contact first name, last name, and email are required.');
      return;
    }
    setSigSaving(true);
    try {
      const res = await api.post('/accounts/from-signature', {
        text: sigText,
        account_type: formData.account_type,
        company_name: company_name.trim(),
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        address_line1: address_line1.trim() || undefined,
        address_line2: address_line2.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip_code: zip_code.trim() || undefined,
        country: country.trim() || formData.country || undefined,
        contact_first_name: contact_first_name.trim(),
        contact_last_name: contact_last_name.trim(),
        contact_email: contact_email.trim(),
        contact_phone: phone.trim() || undefined,
        status: formData.status,
        payment_terms: formData.payment_terms,
        country: formData.country
      });
      if (res.status === 202 && res.data?.data?.request_submitted) {
        setDuplicateDialog({
          open: true,
          existing: res.data?.data?.existing_account || null,
          serverMessage:
            res.data?.message ||
            'A company with the same name already exists. Your request was submitted to the administrator for review. If a contact was included, it will be created when the request is approved.'
        });
        return;
      }
      toast.success(res.data?.message || 'Account and contact created.');
      const accId = res.data?.data?.id;
      navigate(accId ? `/accounts/${accId}` : '/accounts');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create from signature');
    } finally {
      setSigSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/accounts')}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Account' : 'New Account'}</Typography>
      </Box>

      {!isEdit && (
        <Card
          sx={{
            mb: 3,
            border: '2px solid #dc2626',
            bgcolor: '#fef2f2',
            boxShadow: '0 0 0 2px rgba(220,38,38,0.12)'
          }}
        >
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#b91c1c' }}>
                Generate the Account and Contact Details from Signature Text
              </Typography>
              <Button
                variant="contained"
                startIcon={<ContentPasteIcon />}
                sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                onClick={() => {
                  setSigOpen((wasOpen) => {
                    if (wasOpen) {
                      setSigParsed(false);
                      setSigHints([]);
                    }
                    return !wasOpen;
                  });
                }}
              >
                {sigOpen ? 'Hide' : 'Show'}
              </Button>
            </Stack>
            {sigOpen ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cut and paste the block from an email signature (name, company, phone, site, email). We infer fields using patterns; you can correct them before creating the account and primary contact.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Signature text"
                  value={sigText}
                  onChange={(e) => {
                    setSigText(e.target.value);
                    setSigParsed(false);
                  }}
                  placeholder="Paste signature here…"
                  sx={{ mb: 2 }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                    onClick={handleParseSignature}
                    disabled={sigParsing}
                  >
                    {sigParsing ? <CircularProgress size={22} /> : 'Parse & show extracted fields'}
                  </Button>
                </Stack>
                {sigParsed ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      How values were inferred
                    </Typography>
                    <List dense disablePadding sx={{ mb: 2, bgcolor: 'action.hover', borderRadius: 1, py: 0.5 }}>
                      {sigHints.length ? (
                        sigHints.map((h, i) => (
                          <ListItem key={i} disablePadding sx={{ py: 0.25, alignItems: 'flex-start' }}>
                            <ListItemText primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} primary={h} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="No hint text returned." />
                        </ListItem>
                      )}
                    </List>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Review and edit before creating
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Company name"
                          value={sigFields.company_name}
                          onChange={(e) => updateSigField('company_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Phone"
                          value={sigFields.phone}
                          onChange={(e) => updateSigField('phone', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Website (full URL)"
                          value={sigFields.website}
                          onChange={(e) => updateSigField('website', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address line 1"
                          value={sigFields.address_line1}
                          onChange={(e) => updateSigField('address_line1', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Address line 2"
                          value={sigFields.address_line2}
                          onChange={(e) => updateSigField('address_line2', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={sigFields.city}
                          onChange={(e) => updateSigField('city', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          value={sigFields.state}
                          onChange={(e) => updateSigField('state', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="ZIP / postal code"
                          value={sigFields.zip_code}
                          onChange={(e) => updateSigField('zip_code', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Country"
                          value={sigFields.country}
                          onChange={(e) => updateSigField('country', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Contact first name"
                          value={sigFields.contact_first_name}
                          onChange={(e) => updateSigField('contact_first_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Contact last name"
                          value={sigFields.contact_last_name}
                          onChange={(e) => updateSigField('contact_last_name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Contact email"
                          type="email"
                          value={sigFields.contact_email}
                          onChange={(e) => updateSigField('contact_email', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Account type and status below apply to this action. Duplicate company names follow the same administrator review flow as manual entry; the contact is created when the duplicate request is approved.
                    </Alert>
                    <Button
                      variant="contained"
                      onClick={handleAddAccountFromSignature}
                      disabled={sigSaving}
                      sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #6366f1 100%)' }}
                    >
                      {sigSaving ? <CircularProgress size={22} color="inherit" /> : 'Add account from signature'}
                    </Button>
                  </>
                ) : null}
              </Box>
            ) : null}
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Account Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label={<RequiredLabel>Company Name</RequiredLabel>} 
                  name="company_name" 
                  value={formData.company_name} 
                  onChange={handleChange} 
                  required
                  error={!!validationErrors.company_name}
                  helperText={validationErrors.company_name ? 'Company Name is required' : ''}
                  sx={getFieldErrorSx(validationErrors.company_name)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!validationErrors.account_type}>
                  <InputLabel><RequiredLabel>Account Type</RequiredLabel></InputLabel>
                  <Select 
                    name="account_type" 
                    value={formData.account_type} 
                    label="Account Type *" 
                    onChange={handleChange}
                    sx={getSelectErrorSx(validationErrors.account_type)}
                  >
                    <MenuItem value="client">Client</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                    <MenuItem value="hosting_company">Hosting Company *</MenuItem>
                  </Select>
                  {validationErrors.account_type ? (
                    <FormHelperText error>Account Type is required</FormHelperText>
                  ) : (
                    <FormHelperText>
                      <Typography variant="caption" color="text.secondary" component="span">
                        <strong>Hosting Company *</strong> means the company hosting this software / tenant. There can be
                        more than one hosting company in the same tenant.
                      </Typography>
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth><InputLabel>Status</InputLabel>
                  <Select name="status" value={formData.status} label="Status" onChange={handleChange}>
                    <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="prospect">Prospect</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    name="industry"
                    value={formData.industry ?? ''}
                    label="Industry"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {industryChoices.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Website
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel id="account-website-protocol-label">Prefix</InputLabel>
                    <Select
                      labelId="account-website-protocol-label"
                      value={websiteProtocol}
                      label="Prefix"
                      onChange={(e) => setWebsiteProtocol(e.target.value)}
                    >
                      <MenuItem value="https://">https://</MenuItem>
                      <MenuItem value="http://">http://</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    placeholder="www.example.com or example.com/path"
                    value={websiteHost}
                    onChange={(e) => setWebsiteHost(e.target.value)}
                    helperText="Choose http:// or https://, then enter the site without repeating the prefix."
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <InternationalPhoneField label="Phone" name="phone" value={formData.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InternationalPhoneField label="Fax" name="fax" value={formData.fax} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <StructuredAddressFields
                  values={{
                    country: formData.country,
                    state: formData.state,
                    city: formData.city,
                    zip_code: formData.zip_code,
                    address_line1: formData.address_line1,
                    address_line2: formData.address_line2,
                  }}
                  onPatch={patchFormData}
                />
              </Grid>
              <Grid item xs={12}><TextField fullWidth label="Notes" name="notes" value={formData.notes} onChange={handleChange} multiline rows={3} /></Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={contacts}
                  getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                  value={selectedContacts}
                  onChange={(event, newValue) => setSelectedContacts(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Associated Contacts" placeholder="Select contacts..." />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={`${option.first_name} ${option.last_name}`} {...getTagProps({ index })} color="primary" variant="outlined" />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/accounts')}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>{saving ? 'Saving...' : 'Save Account'}</Button>
        </Box>
      </form>

      <Dialog
        open={duplicateDialog.open}
        onClose={() => {
          setDuplicateDialog({ open: false, existing: null, serverMessage: '' });
          navigate('/accounts');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request submitted for review</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {duplicateDialog.serverMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The account you tried to create matches an existing company. An administrator will review your request. You can track it under{' '}
            <strong>My Items → My account status</strong>.
          </Typography>
          {duplicateDialog.existing ? (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Existing account — use this reference in Accounts search
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {duplicateDialog.existing.company_name}
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ mt: 1 }}>
                Reference: {duplicateDialog.existing.account_code ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Copy the reference and paste it into the Accounts list search to open the current record.
              </Typography>
              {duplicateDialog.existing?.id ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }} fontFamily="monospace">
                  Record ID: {duplicateDialog.existing.id}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const code = duplicateDialog.existing?.account_code;
                    if (code && navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(String(code));
                      toast.success('Reference copied');
                    }
                  }}
                  disabled={!duplicateDialog.existing?.account_code}
                >
                  Copy reference
                </Button>
                {duplicateDialog.existing?.id ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const id = duplicateDialog.existing?.id;
                      if (id && navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(String(id));
                        toast.success('Record ID copied');
                      }
                    }}
                  >
                    Copy record ID
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setDuplicateDialog({ open: false, existing: null, serverMessage: '' });
              navigate('/accounts');
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountForm;
